// State machine for creating a new broadcast from upload:
// 1. create backdated broadcast with stream_source: imported
// 2. POST to create new placeholder for upload slot on the broadcast
// 3. upload into the presigned URL given by #2
// 4. monitor progress of upload
// 5. poke API to update status of broadcast upload and initiate transcode
// 6. redirect to broadcast page to monitor transcode status

import { adjustBroadcastDatesForCrossingMidnight } from '../../utils/date';

export default function ModalUploadEventController(
  $uibModalInstance,
  $scope,
  $http,
  toaster,
  $timeout,
  $q,
  BandwidthTestClient,
  Broadcast,
  $rootScope,
  broadcast,
  highlights
) {
  'ngInject';

  this.FeatureFlags = $rootScope.account.feature_flags;
  this.error = null;
  this.highlights = highlights;

  if (broadcast == null) {
    this.mode = 'new';
    broadcast = {
      name: '',
    };
  } else {
    this.mode = 'replacement';
  }

  this.maxDate = new Date();
  // Fake the EditOrCreateEventService for this context
  this.yesterday = moment().add(-1, 'day').set({ hour: 0, minute: 0, second: 0 }).toDate();
  this.service = {
    broadcast: broadcast,
    startDate: moment(this.yesterday).clone().toDate(),
    startTime: moment(this.yesterday).clone().set({ hour: 12, minute: 0, second: 0 }).toDate(),
    maxBroadcastDurationHours: $rootScope.account.max_broadcast_duration_hrs,
    resolutionLimit: $rootScope.account.resolution_limit,
  };

  this.file = null;

  const estimateUploadAndProcessingTime = () => {
    $timeout.cancel(this._videoDurationPoll);

    // If we don't have an available bandwidth yet, clear the estimates and return
    if (!this.availableBandwidth) {
      delete this.uploadEstimateMs;
      delete this.processingEstimateMs;
      return;
    }
    const bandwidth = this.availableBandwidth;

    // If we don't have a file or size/duration yet, clear the estimates and return
    if (!this.file || !this.file.file || !this.file.file.size) {
      delete this.uploadEstimateMs;
      delete this.processingEstimateMs;
      return;
    }
    const uploadSize = this.file.file.size;

    if (!this.file.videoDuration) {
      // Unfortunately it seems like $scope.$watch doesn't always fire when the videoDuration
      // property gets added. This is a little dirty, but kind of "polls" for the presence of that property
      delete this.uploadEstimateMs;
      delete this.processingEstimateMs;
      this._videoDurationPoll = $timeout(estimateUploadAndProcessingTime, 250);
      return;
    }
    const videoDurationSeconds = this.file.videoDuration;

    // bandwidth is in bps (bits per second), but uploadSize is bytes, so do math; also convert seconds to ms
    this.uploadEstimateMs = ((uploadSize * 8.0) / bandwidth) * 1000.0;

    // XXX: Here is where we could replace this logic with actual calls to the upload service to
    // get more accurate estimates. But for now, assume a couple minutes of overhead for task
    // assignment, spin-up, shutdown, daemon polling, etc.; and assume that processing duration
    // matches video duration.
    this.processingEstimateMs =
      2 * 60 * 1000 + // overhead
      videoDurationSeconds * 1000; // video duration
  };
  $scope.$on('$destroy', () => $timeout.cancel(this._videoDurationPoll));

  // Start a bandwidth test in the background when this dialog opens
  BandwidthTestClient.runTestOrUseRecentCached().then((result) => {
    if (result && result.success && result.availableBandwidth) {
      this.availableBandwidth = result.availableBandwidth;
      estimateUploadAndProcessingTime();
    }
  });
  $scope.$on('$destroy', BandwidthTestClient.cancelIfInFlight);

  // Watch for file changes to estimate upload duration
  $scope.$watch(() => this.file, estimateUploadAndProcessingTime);

  this.submit = () => {
    if (!this.file) {
      this.error = 'Please select a file.';
      return;
    }
    if (
      this.mode == 'new' &&
      (!this.service.broadcast.name || !this.service.startDate || !this.service.startTime)
    ) {
      this.error = 'Please enter all required fields.';
      return;
    }

    var startDateTime = moment(this.service.startDate).clone();
    startDateTime.set({
      hour: moment(this.service.startTime).get('hour'),
      minute: moment(this.service.startTime).get('minute'),
      second: 0,
    });

    if (startDateTime.toDate() > this.maxDate) {
      this.error = 'Future dated broadcasts are not allowed';
      return;
    }

    this.error = null;
    this.submitting = true;

    var promise;
    if (this.mode == 'new') {
      // Use <video> duration, rounded up to nearest minute, for at least 1 minute.
      var videoDurationSeconds = this.file.videoDuration || 60 * 60; //assume 1hr
      var videoDurationMinutes = Math.max(1, Math.ceil(videoDurationSeconds / 60));
      this.service.endTime = moment(this.service.startTime)
        .clone()
        .add(videoDurationMinutes, 'minutes')
        .toDate();
      if (this.service.endTime > this.maxDate) {
        this.service.endTime = this.maxDate;
      }
      var dates = adjustBroadcastDatesForCrossingMidnight(
        this.service.startDate,
        this.service.startTime,
        this.service.endTime,
        this.service.maxBroadcastDurationHours
      );

      if (moment(dates.stops_at).toDate() > this.maxDate) {
        this.submitting = false;
        this.error = `Invalid broadcast date/time. Please set the start time earlier to avoid having the end in the future (computed video duration of ${videoDurationMinutes} minutes).`;
        return;
      }

      var newBroadcast = Broadcast.save({
        name: this.service.broadcast.name,
        starts_at: dates.starts_at,
        stops_at: dates.stops_at,
        stream_source: 'imported',
        is_private: true,
      });
      promise = newBroadcast.$promise.then(() => {
        console.log('Broadcast saved, now uploading video', newBroadcast);
        return newBroadcast;
      });
    } else if (this.mode == 'replacement') {
      // Replacement is much simpler; no need to create a new broadcast.
      promise = $q((res, _) => res(broadcast));
    } else {
      throw Error(`Unsupported mode: ${this.mode}`);
    }

    promise
      .then((b) => {
        broadcast = b;
        return this.file.upload({ broadcast_id: broadcast.id });
      })
      .then((response) => {
        console.log('Upload completed', response);
        toaster.pop('success', 'Successfully uploaded broadcast');
        $uibModalInstance.close(broadcast);
      })
      .catch((err) => {
        this.submitting = false;
        this.error = 'An error occurred while trying to upload the broadcast.';
        console.error(err);
        toaster.pop(
          'warning',
          'An error occurred while trying to save the broadcast.',
          'Please refresh and try again.',
          0 //don't auto-timeout
        );
      });
  };

  this.cancel = () => $uibModalInstance.dismiss('cancel');
}



// WEBPACK FOOTER //
// ./app/pages/events/modal-upload-event.js